/**
 * @file agents.api.js
 * @module AgentsAPI
 * @description HTTP API wrapper for agent/agency endpoints. Methods return Axios promises.
 */

import { axiosInstance } from "./axios"

export const AgentsAPI = {
    /**
     * Retrieve agents for a customer with pagination and optional query.
     * @param {string} customerId - Customer identifier.
     * @param {number} limit - Number of items per page.
     * @param {number} page - Page number.
     * @param {string} query - Optional query string.
     * @param {string} token - Optional auth token.
     * @returns {Promise} Axios promise resolving to agents list.
     */
    getAgents: async (customerId, limit, page, query, token = "") => {
        return await axiosInstance(token).get(`/customers/${customerId}/agents?limit=${limit}&page=${page}${query ? `&${query}` : ""}`);
    },

    /**
     * Create a new agent (user) for the system.
     * @param {Object} body - Agent payload.
     * @param {string} token - Optional auth token.
     * @returns {Promise} Axios promise resolving to created agent.
     */
    createAgent: (body, token = "") => {
        return axiosInstance(token).post(`/users?origin=rhomtrip_travel`, body);
    },

    /**
     * Retrieve an agent by its identifier.
     * @param {string} agentId - Agent identifier.
     * @param {string} token - Optional auth token.
     * @returns {Promise} Axios promise resolving to agent data.
     */
    getAgentById: (agentId, token = "") => {
        return axiosInstance(token).get(`/users/${agentId}`);
    },

    /**
     * Delete an agent by id.
     * @param {string} _id - Agent identifier.
     * @param {string} token - Optional auth token.
     * @returns {Promise} Axios promise resolving to deletion result.
     */
    deleteAgentById: (_id, token = "") => {
        return axiosInstance(token).delete(`/users/${_id}`);
    },

    /**
     * Update an agent by id (full update).
     * @param {string} _id - Agent identifier.
     * @param {Object} body - Updated agent payload.
     * @param {string} token - Optional auth token.
     * @returns {Promise} Axios promise resolving to updated agent.
     */
    updateAgentById: (_id, body, token = "") => {
        return axiosInstance(token).put(`/users/${_id}?origin=rhomtrip_travel`, body);
    },

    /**
     * Patch one or more attributes of an agent.
     * @param {string} _id - Agent identifier.
     * @param {Object} body - Partial attributes to update.
     * @param {string} token - Optional auth token.
     * @returns {Promise} Axios promise resolving to updated agent.
     */
    updateSameAttributeById: (_id, body, token = "") => {
        return axiosInstance(token).patch(`/users/${_id}`, body);
    },

    /**
     * Delete many agents by payload.
     * @param {Object} body - Payload describing agents to delete.
     * @param {string} token - Optional auth token.
     * @returns {Promise} Axios promise resolving to deletion result.
     */
    deleteManyAgentsById: (body, token = "") => {
        return axiosInstance(token).put(`/users/delete/many`, body);
    },

    /**
     * Get agent options for a customer (lightweight list for selects).
     * @param {string} customerId - Customer identifier.
     * @param {string} token - Optional auth token.
     * @returns {Promise} Axios promise resolving to options list.
     */
    getOptionsAgentsByCustomerId(customerId, token = "") {
        return axiosInstance(token).get(`/customers/${customerId}/agent-options`);
    },

    /**
     * Update a partner's reference code.
     * @param {string} partnerId - Partner identifier.
     * @param {string} referenceCode - New reference code.
     * @param {string} [token=""] - Optional auth token.
     * @returns {Promise} Axios promise resolving to update result.
     */
    updatePartnerReferenceCode: (partnerId, referenceCode, token = "") => {
        return axiosInstance(token).patch(`/partners/${partnerId}/reference-code`, { referenceCode })
    }
}